import { Signal } from '@crown/signal';
import { evaluateGate } from '../context/gate';
import type { RenderContext } from '../context/types';
import type {
  ElementRef,
  BoundaryNode,
  Child,
  ClientNode,
  DeferNode,
  DynAttr,
  EachNode,
  ElNode,
  IfNode,
  Instruction,
  RegionNode,
  PropInput,
  SigNode,
  SigValue,
  TplNode,
} from '../ir/types';
import { holeChildren, holeIsReader } from '../ir/constructors';
import { isFunction, isNumber, isString } from '../ir/guards';
import { attrSlot, eventSlot } from '../ir/props';
import {
  instructionChildren,
  InstructionError,
  normaliseChildren,
  readChild,
  rowNeedsMarkers,
} from '../ir/values';
import { serialiseProps } from '../islands/props';
import {
  bindDyn,
  bindEvents,
  bindHandler,
  type Disposer,
  type Teardown,
  disposeAll,
  type ErrorSink,
} from './bindings';
import { END_MARKER } from './complete';
import { checkFocusBeforeRemoval, checkNameAfterWrite } from './dev';
import { focusPlace, restoreFocus } from './focus';
import {
  isComment,
  isCommentNode,
  isElement,
  isText,
  isTextNode,
} from './nodes';
import {
  type CreatedRow,
  ListItems,
  reconcileRows,
  RowIndex,
  type RowRecord,
  rowRecord,
} from './each';
import { effect } from './effect';
import { schedule } from './scheduler';
import {
  cloneTemplate,
  freshSlotTargets,
  HOLE_END,
  HOLE_START,
  holeEnd,
  slotNamespace,
  slotTargets,
} from './template';

export interface MountOptions {
  readonly before?: Node | null;
  readonly ctx?: RenderContext | null;
}

/**
 * What a subtree needs while it is created: the gate context, where its errors
 * go, and the namespace its elements are created in.
 *
 * `ns` is absent for HTML, which is all but the inside of an `<svg>` or
 * `<math>`. It has to travel with the subtree rather than be read off the tag,
 * because `a`, `title`, `style` and `script` exist in both languages, and
 * because a `sig` inside an icon creates its nodes long after its parent did.
 */
export interface Scope {
  readonly ctx: RenderContext | null;
  readonly sink: ErrorSink;
  readonly ns?: string;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const MATHML_NS = 'http://www.w3.org/1998/Math/MathML';

/** The namespace an element is created in, given the one its parent was created in. */
function elementNamespace(
  tag: string,
  ns: string | undefined,
): string | undefined {
  if (tag === 'svg') return SVG_NS;
  if (tag === 'math') return MATHML_NS;
  return ns;
}

/** The namespace that element's children are created in. `foreignObject` is the way back to HTML. */
function childNamespace(
  tag: string,
  ns: string | undefined,
): string | undefined {
  return ns === SVG_NS && tag === 'foreignObject' ? undefined : ns;
}

const consoleSink: ErrorSink = (error) => {
  console.error('[@crown/framework] render failed', error);
};

const templates = new Map<string, HTMLTemplateElement>();

export function comment(data: string): Comment {
  return document.createComment(data);
}

function removeNodes(nodes: readonly Node[]): void {
  for (const node of nodes) {
    checkFocusBeforeRemoval(node);
    node.parentNode?.removeChild(node);
  }
}

function insertBefore(nodes: readonly Node[], reference: Node): void {
  const parent = reference.parentNode;
  if (!parent) return;
  for (const node of nodes) parent.insertBefore(node, reference);
}

/** Writes the island attributes an element carries, and marks it live so the islands runtime leaves it alone. */
export function markIsland(
  element: Element,
  node: ElNode,
  ctx: RenderContext | null,
): void {
  if (!node.island) return;
  element.setAttribute('data-c-island', node.island.component);
  element.setAttribute('data-c-props', serialiseProps(node.island.props));
  element.setAttribute('data-c-load', node.island.load);
  if (node.island.remote) {
    element.setAttribute(
      'data-c-remote',
      `${node.island.remote.name}@${node.island.remote.manifest}`,
    );
  }
  element.setAttribute('data-c-hydrated', '');
  ctx?.islands.add(node.island.component, node.island.remote);
}

function createElement(
  node: ElNode,
  scope: Scope,
  disposers: Teardown[],
): Element {
  const ns = elementNamespace(node.tag, scope.ns);
  const element =
    ns === undefined
      ? document.createElement(node.tag)
      : document.createElementNS(ns, node.tag);
  const childNs = childNamespace(node.tag, ns);
  const inner: Scope = childNs === scope.ns ? scope : { ...scope, ns: childNs };
  let chosen: string | null = null;
  for (const [name, value] of Object.entries(node.attrs)) {
    if (namesAChild(element, name)) chosen = value;
    else element.setAttribute(name, value);
  }
  if (node.key !== undefined) element.setAttribute('data-c-key', node.key);
  const late: DynAttr[] = [];
  for (const dyn of node.dyn) {
    if (
      dyn.kind !== 'class' &&
      dyn.kind !== 'style' &&
      namesAChild(element, dyn.name)
    )
      late.push(dyn);
    else bindDyn(element, dyn, disposers, scope.sink);
  }
  bindEvents(element, node.on, disposers);
  markIsland(element, node, scope.ctx);
  for (const child of node.children) {
    for (const created of createNodes(child, inner, disposers))
      element.appendChild(created);
  }
  if (chosen !== null) {
    // SAFETY: `chosen` is only set for an element `namesAChild` found to be a <select>.
    (element as HTMLSelectElement).value = chosen;
  }
  for (const dyn of late) bindDyn(element, dyn, disposers, scope.sink);
  if (node.ref) attachRef(element, node.ref, disposers);
  return element;
}

/**
 * A `<select>`'s `value` or `selectedIndex` picks one of its options, so it is
 * written once they are in it: written before, it matches nothing and the
 * select shows its first option. Written as an attribute it means nothing to
 * a select at all, so either spelling is taken as the property.
 */
function namesAChild(element: Element, name: string): boolean {
  return (
    (name === 'value' || name === 'selectedIndex') &&
    element.localName === 'select'
  );
}

/** Hands an element to its ref once it is built, keeping what the ref returns for disposal. */
export function attachRef(
  element: Element,
  ref: ElementRef,
  disposers: Teardown[],
): void {
  const cleanup = ref(element);
  if (isFunction(cleanup)) disposers.push(cleanup);
}

/** The text a `sig` writes when its value is not a subtree. */
function sigText(value: SigValue): string {
  return value === null || value === undefined ? '' : String(value);
}

/** What a `sig` currently shows between its markers. */
interface SigContent {
  disposers: Teardown[];
  text: Text | null;
}

/** The nodes for one `sig` value, and the text node when it wrote text. */
interface SigNodes {
  nodes: Node[];
  text: Text | null;
}

function sigContent(
  value: SigValue,
  tree: readonly Instruction[] | null,
  scope: Scope,
  disposers: Teardown[],
): SigNodes {
  if (tree) return { nodes: createList(tree, scope, disposers), text: null };
  const text = document.createTextNode(sigText(value));
  return { nodes: [text], text };
}

/** The `sig`'s end marker, which a hydration only has once its content is claimed. */
export interface SigEnd {
  current: Comment | null;
}

/**
 * Installs the effect behind a `sig`: one read, on its first run.
 *
 * Text writes to the text node it already has; a value that is an instruction
 * replaces whatever sits between the markers with that subtree. `onFirst`
 * turns that first value into content — created for a mount, claimed from the
 * server's HTML for a hydration.
 */
function installSig(
  node: SigNode,
  scope: Scope,
  disposers: Teardown[],
  endRef: SigEnd,
  onFirst: (value: SigValue, tree: readonly Instruction[] | null) => SigContent,
  marker = 'c:s',
): void {
  let current: SigContent = { disposers: [], text: null };
  let first = true;
  disposers.push(
    effect(() => {
      let value: SigValue;
      try {
        value = node.read();
      } catch (error) {
        first = false;
        scope.sink(error);
        return;
      }
      const tree = instructionChildren(value);
      if (first) {
        first = false;
        current = Signal.subtle.untrack(() => onFirst(value, tree));
        return;
      }
      if (!tree && current.text) {
        current.text.data = sigText(value);
        checkNameAfterWrite(current.text);
        return;
      }
      const fresh: Teardown[] = [];
      const { nodes, text } = Signal.subtle.untrack(() =>
        sigContent(value, tree, scope, fresh),
      );
      // The old subtree stops now, so none of its bindings runs again in
      // this flush; its nodes leave with the next DOM write.
      disposeAll(current.disposers);
      current = { disposers: fresh, text };
      schedule(() => {
        const end = endRef.current;
        if (!end) return;
        const start = findStart(end, marker);
        const focus = start ? focusPlace(start, end) : null;
        removeBetween(start, end, focus !== null);
        insertBefore(nodes, end);
        if (focus) restoreFocus(focus, nodes);
      });
    }),
  );
  disposers.push(() => disposeAll(current.disposers));
}

/** A `sig` and its markers: a text node, or the subtree its reader yielded. */
export function createSig(
  node: SigNode,
  scope: Scope,
  disposers: Teardown[],
): Node[] {
  const end = comment('/c:s');
  const content: Node[] = [];
  installSig(node, scope, disposers, { current: end }, (value, tree) => {
    const fresh: Teardown[] = [];
    const { nodes, text } = sigContent(value, tree, scope, fresh);
    content.push(...nodes);
    return { disposers: fresh, text };
  });
  return [comment('c:s'), ...content, end];
}

/** The same effect over content the server wrote; `claim` adopts it on the first read. */
export function adoptSig(
  node: SigNode,
  scope: Scope,
  disposers: Teardown[],
  endRef: SigEnd,
  claim: (
    value: SigValue,
    tree: readonly Instruction[] | null,
    inner: Teardown[],
  ) => Text | null,
  marker = 'c:s',
): void {
  installSig(
    node,
    scope,
    disposers,
    endRef,
    (value, tree) => {
      const inner: Teardown[] = [];
      const text = claim(value, tree, inner);
      return { disposers: inner, text };
    },
    marker,
  );
}

/** Fills a template hole's content; a mount creates it, a hydration claims it. */
export type HoleFiller = (
  start: Comment,
  end: Comment,
  children: readonly Instruction[],
  scope: Scope,
  disposers: Teardown[],
) => void;

/**
 * Binds a template's slots under its element. Attribute text the template
 * holds is already on the element; a hydration finds the server's copy there
 * too, so only a mount writes it.
 */
export function fillTemplate(
  root: Element,
  node: TplNode,
  scope: Scope,
  disposers: Teardown[],
  hydrating: boolean,
  fillHole: HoleFiller,
): void {
  const { slots } = node.template;
  if (slots.length === 0) return;
  // A fresh copy is found by position; the server's copy holds content in its
  // holes, so a hydration counts a hole and its content as one.
  const targets = hydrating
    ? slotTargets(root, node.template)
    : freshSlotTargets(root, node.template, scope.ns);
  // A select's value is bound once its options are in it, after every hole.
  let chosen:
    | { readonly select: Element; readonly binding: DynAttr | string }[]
    | null = null;
  // An index loop: this runs once per slot of every row, and an iterator
  // allocates where a counter does not.
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const target = targets[i];
    if (!slot || !target) continue;
    const hole = node.holes[i];
    if (slot.kind === 'attr' || slot.kind === 'event') {
      // SAFETY: an attribute or event slot's target is the element its path
      // names; `template()` checked the paths against the markup when it was made.
      const element = target as Element;
      // SAFETY: an attribute or event slot's value is a prop as the compiler wrote it.
      const prop = hole as PropInput;
      if (slot.kind === 'attr') {
        const binding = attrSlot(slot.name, prop);
        if (binding === null) continue;
        if (namesAChild(element, slot.name)) {
          (chosen ??= []).push({ select: element, binding });
          continue;
        }
        if (isString(binding)) {
          if (!hydrating) element.setAttribute(slot.name, binding);
        } else {
          bindDyn(element, binding, disposers, scope.sink);
        }
      } else {
        const binding = eventSlot(slot.name, prop);
        if (!binding) continue;
        // A server copy stays on the page after its bindings go, so it keeps a way to undo them.
        if (hydrating) bindEvents(element, [binding], disposers);
        else bindHandler(element, binding, disposers);
      }
    } else {
      const ns = slotNamespace(slot.ns, scope.ns);
      const inner = ns === scope.ns ? scope : { ...scope, ns };
      // A sole slot in a fresh copy is one text node: a reader writes it, and
      // markers go in only if the value turns out to be markup.
      // SAFETY: a content slot's value is a child, as the compiler wrote it.
      const child = hole as Child;
      // A server render writes a sole slot's text the same way, bare.
      if (isTextNode(target)) {
        textSlot(target, child, holeIsReader(slot, child), inner, disposers);
        continue;
      }
      // SAFETY: a content slot's target, where it is not a fresh copy's text, is its opening marker.
      const start = target as Comment;
      // SAFETY: a fresh copy's closing marker follows its opening one at once; a hydration finds its own.
      const end = hydrating ? holeEnd(target) : (target.nextSibling as Comment);
      // Otherwise a fresh hole is just its two markers, and a reader in it is
      // bound straight to its effect with no instruction in between.
      if (!hydrating && holeIsReader(slot, child)) {
        // SAFETY: the reader is bound as a child reader always has been here; `holeReader` renders what it yields.
        holeReader(child as () => SigValue, inner, disposers, end);
        continue;
      }
      fillHole(start, end, holeChildren(child), inner, disposers);
    }
  }
  if (!chosen) return;
  for (const { select, binding } of chosen) {
    if (isString(binding)) {
      // SAFETY: only an element `namesAChild` found to be a <select> is queued here.
      (select as HTMLSelectElement).value = binding;
    } else {
      bindDyn(select, binding, disposers, scope.sink);
    }
  }
}

/**
 * A template hole holding one reader, as one effect: text is written into
 * its node in place, and only a value that is a subtree swaps what sits
 * between the hole's markers. The hole's markers are the reader's own.
 */
/**
 * A reader bound to a hole. The hole is its two markers, or — for a sole slot
 * in a fresh copy — the one text node the copy already holds, which gives way
 * to markers the first time the value is markup rather than text.
 */
function holeReader(
  read: () => SigValue,
  scope: Scope,
  disposers: Teardown[],
  hole: Comment | Text,
): void {
  let text: Text | null = isTextNode(hole) ? hole : null;
  let end: Comment | null = isCommentNode(hole) ? hole : null;
  let inner: Teardown[] | null = null;
  let filled = text !== null;
  const node = Signal.subtle.effectNode(() => {
    let value: SigValue;
    try {
      // Read through as the server does: a reader that yields a reader (a
      // `() => string` prop placed straight into template text) is followed.
      value = readChild(read);
    } catch (error) {
      scope.sink(error);
      return;
    }
    const plain = isString(value) || isNumber(value);
    if (plain && text) {
      text.data = String(value);
      checkNameAfterWrite(text);
      return;
    }
    const tree = plain ? null : instructionChildren(value);
    if (!tree && text) {
      text.data = sigText(value);
      checkNameAfterWrite(text);
      return;
    }
    // Markup in a slot that has only been a text node: it takes markers now.
    if (!end && text) {
      const start = comment(HOLE_START);
      end = comment(HOLE_END);
      text.replaceWith(start, end);
      text = null;
      filled = false;
    }
    if (!end) return;
    // First run, or text giving way to a subtree or back: rebuild the hole.
    if (inner) disposeAll(inner);
    if (filled) removeBetween(findStart(end, HOLE_START), end);
    filled = true;
    const parent = end.parentNode;
    if (!parent) return;
    if (tree) {
      text = null;
      if (!inner) {
        inner = [];
        const own = inner;
        disposers.push(() => disposeAll(own));
      }
      const own = inner;
      const nodes = Signal.subtle.untrack(() => createList(tree, scope, own));
      for (const created of nodes) parent.insertBefore(created, end);
    } else {
      text = document.createTextNode(sigText(value));
      parent.insertBefore(text, end);
    }
  });
  if (node) disposers.push(node);
}

/** A sole slot's value: text written into its node, a reader bound to it, or markup between markers. */
function textSlot(
  target: Text,
  hole: Child,
  isReader: boolean,
  scope: Scope,
  disposers: Teardown[],
): void {
  if (isReader) {
    // SAFETY: `isReader` is `holeIsReader` of this very hole: a reader of a child.
    holeReader(hole as () => SigValue, scope, disposers, target);
    return;
  }
  const children = holeChildren(hole);
  const only = children.length === 1 ? children[0] : undefined;
  if (children.length === 0) {
    target.data = '';
    return;
  }
  if (only?.t === 'text') {
    target.data = only.value;
    return;
  }
  if (only?.t === 'sig') {
    holeReader(only.read, scope, disposers, target);
    return;
  }
  const start = comment(HOLE_START);
  const end = comment(HOLE_END);
  target.replaceWith(start, end);
  createHole(start, end, children, scope, disposers);
}

/** A hole holding one reader is that reader's markers; anything else is built between them. */
const createHole: HoleFiller = (_start, end, children, scope, disposers) => {
  const only = children.length === 1 ? children[0] : undefined;
  if (only?.t === 'sig') {
    holeReader(only.read, scope, disposers, end);
    return;
  }
  insertBefore(createList(children, scope, disposers), end);
};

function createTpl(
  node: TplNode,
  scope: Scope,
  disposers: Teardown[],
): Element {
  const root = cloneTemplate(node.template, scope.ns);
  fillTemplate(root, node, scope, disposers, false, createHole);
  return root;
}

/** Installs the effect that swaps `if` branches between the markers. */
/**
 * The effect that swaps an `if`'s branch, made before the branch it starts
 * with. Effects run in the order they were made, so this one runs ahead of
 * every effect inside the branch: when the condition flips, the old branch is
 * disposed before any of its bindings can run against a value that no longer
 * satisfies it. The returned function hands it the markers' end and the
 * starting branch's disposers once they exist.
 */
export function installIf(
  node: IfNode,
  scope: Scope,
  disposers: Teardown[],
  shown: boolean,
): (end: Comment, shownDisposers: Teardown[]) => void {
  let end: Comment | null = null;
  let currentDisposers: Teardown[] = [];
  let showing = shown;
  disposers.push(
    effect(() => {
      // Only a change of side rebuilds: a condition that re-ran to the same
      // answer leaves the branch, and everything live inside it, as it is.
      const cond = Boolean(node.cond());
      if (cond === showing) return;
      showing = cond;
      // The old branch stops now, so none of its bindings runs again in this
      // flush; its nodes leave with the next DOM write.
      disposeAll(currentDisposers);
      const active = cond ? node.body() : node.otherwise();
      const fresh: Teardown[] = [];
      const nodes = Signal.subtle.untrack(() =>
        createList(active, scope, fresh),
      );
      currentDisposers = fresh;
      const marker = end;
      schedule(() => {
        if (!marker) return;
        // Everything between the markers goes, not only the nodes this branch
        // was created with: a loop inside the branch may have inserted rows of
        // its own since, and removing the remembered nodes alone left those
        // rows on the page under the next branch's content.
        removeBetween(findStart(marker, 'c:if'), marker);
        insertBefore(nodes, marker);
      });
    }),
  );
  disposers.push(() => disposeAll(currentDisposers));
  return (endMarker, shownDisposers) => {
    end = endMarker;
    currentDisposers = shownDisposers;
  };
}

/** The matching start marker for an end marker, honouring nesting. */
function findStart(end: Comment, data: string): Comment | null {
  let depth = 0;
  for (let n = end.previousSibling; n; n = n.previousSibling) {
    if (!isCommentNode(n)) continue;
    if (n.data === `/${data}`) depth++;
    else if (n.data === data) {
      if (depth === 0) return n;
      depth--;
    }
  }
  return null;
}

/** `focusKept` says the caller moves focus into the new content itself, so its removal is not a mistake. */
function removeBetween(start: Node | null, end: Node, focusKept = false): void {
  if (!start) return;
  let cursor = start.nextSibling;
  while (cursor && cursor !== end) {
    const next = cursor.nextSibling;
    if (!focusKept) checkFocusBeforeRemoval(cursor);
    cursor.parentNode?.removeChild(cursor);
    cursor = next;
  }
}

function createIf(node: IfNode, scope: Scope, disposers: Teardown[]): Node[] {
  const start = comment('c:if');
  const end = comment('/c:if');
  const shownDisposers: Teardown[] = [];
  const cond = Boolean(Signal.subtle.untrack(() => node.cond()));
  const attach = installIf(node, scope, disposers, cond);
  const shown = Signal.subtle.untrack(() =>
    createList(cond ? node.body() : node.otherwise(), scope, shownDisposers),
  );
  attach(end, shownDisposers);
  return [start, ...shown, end];
}

/** Handed to a row template that takes no index; it is never called. */
const NO_INDEX = (): number => {
  throw new InstructionError('This row was built without its index');
};

/** Handed to a row that does not read its item through a reader; it is never called. */
const NO_ITEM = (): never => {
  throw new InstructionError('This row was built without its item reader');
};

/** Builds the rows of an `each`; `rows` may already hold hydrated rows, in which case only the effect is installed. */
export function createEach<T>(
  node: EachNode<T>,
  scope: Scope,
  disposers: Teardown[],
  start: Comment,
  end: Comment,
  rows: Map<string, RowRecord>,
  list: ListItems = new ListItems(node.tracksItem === true),
): Node[] {
  // Rows built here carry no markers of their own: the record keeps their
  // first and last node, and only a hydration needs markers to find them.
  const create = (item: T, index: RowIndex): CreatedRow => {
    const rowDisposers: Teardown[] = [];
    const nodes = Signal.subtle.untrack(() => {
      // SAFETY: the row's item reader yields the items of this list, which are T.
      const readItem = node.tracksItem ? (index.readItem as () => T) : NO_ITEM;
      const children = node.row(
        item,
        node.indexed === false ? NO_INDEX : index.read,
        readItem,
      );
      const only = children.length === 1 ? children[0] : undefined;
      // A row that is one template, as compiled rows are, skips the list walk.
      const created =
        only?.t === 'tpl'
          ? [createTpl(only, scope, rowDisposers)]
          : createList(children, scope, rowDisposers);
      if (!rowNeedsMarkers(children)) return created;
      return [
        comment(`c:row:${node.key(item, index.read())}`),
        ...created,
        comment('/c:row'),
      ];
    });
    return { nodes, disposers: rowDisposers };
  };

  let initial: Node[] | null = null;
  disposers.push(
    effect(() => {
      const items = node.items();
      if (initial === null) {
        initial = [];
        if (rows.size > 0) return;
        const first = initial;
        for (let i = 0; i < items.length; i++) {
          // SAFETY: `i` is below `items.length`, and an item may itself be undefined.
          const item = items[i] as T;
          const key = node.key(item, i);
          // The row map is filled here anyway, so it is the duplicate check too.
          if (rows.has(key))
            throw new InstructionError(
              `each() produced the key "${key}" twice`,
            );
          const index = new RowIndex(i, item, list);
          const created = create(item, index);
          rows.set(key, rowRecord(created, index));
          for (const child of created.nodes) first.push(child);
        }
        return;
      }
      // The effect already runs after the writes that moved the list, so it
      // reconciles now. The loop may have been taken off the page between the
      // read and this run: a loop inside a branch whose condition has just
      // flipped is disposed, and its markers with it.
      if (!end.parentNode) return;
      Signal.subtle.untrack(() =>
        reconcileRows(start, end, rows, items, node.key, create, list),
      );
    }),
  );
  disposers.push(() => {
    for (const row of rows.values()) disposeAll(row.disposers);
    rows.clear();
  });
  return [start, ...(initial ?? []), end];
}

function createStatic(
  hash: string,
  children: readonly Instruction[],
  scope: Scope,
): Node[] {
  // The namespace is part of the key: the same children are different elements
  // inside an `<svg>` than they are in HTML, and both may be on one page.
  const key = scope.ns === undefined ? hash : `${scope.ns} ${hash}`;
  let template = templates.get(key);
  if (!template) {
    template = document.createElement('template');
    for (const node of createList(children, scope, []))
      template.content.appendChild(node);
    templates.set(key, template);
  }
  return Array.from(template.content.cloneNode(true).childNodes);
}

function createRaw(html: string): Node[] {
  const template = document.createElement('template');
  template.innerHTML = html;
  return Array.from(template.content.childNodes);
}

/** Pending content first; when the promise settles the resolved nodes replace it between the markers. */
export function createDefer(
  node: DeferNode,
  scope: Scope,
  disposers: Teardown[],
  start: Comment,
  end: Comment,
  pending: Node[],
): Node[] {
  const pendingDisposers: Teardown[] = [];
  let live = true;
  disposers.push(() => {
    live = false;
    disposeAll(pendingDisposers);
  });
  node
    .resolve()
    .then((resolved) => {
      if (!live) return;
      const fresh: Teardown[] = [];
      const nodes = createList(resolved, scope, fresh);
      disposers.push(() => disposeAll(fresh));
      schedule(() => {
        disposeAll(pendingDisposers);
        let cursor = start.nextSibling;
        while (cursor && cursor !== end) {
          const next = cursor.nextSibling;
          cursor.parentNode?.removeChild(cursor);
          cursor = next;
        }
        insertBefore(nodes, end);
      });
    })
    .catch((cause: unknown) => {
      if (live) scope.sink(cause);
    });
  return [start, ...pending, end];
}

export interface BoundaryScope {
  readonly inner: Scope;
  readonly innerDisposers: Teardown[];
  /** Replaces whatever sits between the markers with the fallback for `error`. */
  showFallback(cause: unknown): void;
}

/** The scope a boundary's children render under: any error a child binding reports swaps the content between the markers for the fallback, once. */
export function boundaryScope(
  node: BoundaryNode,
  scope: Scope,
  disposers: Teardown[],
  end: Comment,
): BoundaryScope {
  let innerDisposers: Teardown[] = [];
  let failed = false;
  const showFallback = (cause: unknown): void => {
    if (failed) return;
    failed = true;
    const fresh: Teardown[] = [];
    const nodes = createList(node.fallback(cause), scope, fresh);
    // The failed content stops now; its nodes leave with the next DOM write.
    disposeAll(innerDisposers);
    innerDisposers = fresh;
    schedule(() => {
      removeBetween(findStart(end, 'c:b'), end);
      insertBefore(nodes, end);
    });
  };
  disposers.push(() => disposeAll(innerDisposers));
  return {
    inner: { ctx: scope.ctx, sink: showFallback },
    get innerDisposers() {
      return innerDisposers;
    },
    showFallback,
  };
}

/** Creation errors and later binding errors both swap the boundary's content for the fallback. */
export function createBoundary(
  node: BoundaryNode,
  scope: Scope,
  disposers: Teardown[],
): Node[] {
  const start = comment('c:b');
  const end = comment('/c:b');
  const boundary = boundaryScope(node, scope, disposers, end);
  let content: Node[];
  try {
    content = createList(
      node.children,
      boundary.inner,
      boundary.innerDisposers,
    );
  } catch (error) {
    disposeAll(boundary.innerDisposers);
    content = createList(node.fallback(error), scope, boundary.innerDisposers);
  }
  return [start, ...content, end];
}

function createRegion(node: RegionNode, scope: Scope): Node[] {
  const element = document.createElement('div');
  element.setAttribute('data-c-island', `${node.name}/${node.expose}`);
  element.setAttribute('data-c-props', serialiseProps(node.props));
  element.setAttribute('data-c-load', 'eager');
  element.setAttribute(
    'data-c-remote',
    `${node.remote.name}@${node.remote.manifest}`,
  );
  scope.ctx?.islands.add(node.expose, node.remote);
  return [element];
}

export function createList(
  nodes: readonly Instruction[],
  scope: Scope,
  disposers: Teardown[],
): Node[] {
  const out: Node[] = [];
  for (const node of nodes) out.push(...createNodes(node, scope, disposers));
  return out;
}

export function createNodes(
  node: Instruction,
  scope: Scope,
  disposers: Teardown[],
): Node[] {
  switch (node.t) {
    case 'el': {
      if (!evaluateGate(node.gate, scope.ctx)) return [];
      const element = createElement(node, scope, disposers);
      return node.island || node.attrs['data-c-element'] !== undefined
        ? [element, comment(END_MARKER)]
        : [element];
    }
    case 'text':
      return [document.createTextNode(node.value)];
    case 'sig':
      return createSig(node, scope, disposers);
    case 'if':
      return evaluateGate(node.gate, scope.ctx)
        ? createIf(node, scope, disposers)
        : [];
    case 'each':
      return evaluateGate(node.gate, scope.ctx)
        ? createEach(
            node,
            scope,
            disposers,
            comment('c:each'),
            comment('/c:each'),
            new Map(),
          )
        : [];
    case 'raw':
      return createRaw(node.html);
    case 'static':
      return createStatic(node.hash, node.children, scope);
    case 'defer': {
      if (!evaluateGate(node.gate, scope.ctx)) return [];
      const pendingDisposers: Teardown[] = [];
      const pending = createList(node.pending, scope, pendingDisposers);
      // Loading, as the server marks it.
      for (const created of pending)
        if (isElement(created) && !created.hasAttribute('aria-busy'))
          created.setAttribute('aria-busy', 'true');
      disposers.push(() => disposeAll(pendingDisposers));
      return createDefer(
        node,
        scope,
        disposers,
        comment('c:defer'),
        comment('/c:defer'),
        pending,
      );
    }
    case 'boundary':
      return createBoundary(node, scope, disposers);
    case 'region':
      return evaluateGate(node.gate, scope.ctx)
        ? createRegion(node, scope)
        : [];
    case 'fragment':
      return createList(node.children, scope, disposers);
    case 'dom':
      return [node.node];
    case 'tpl':
      return [createTpl(node, scope, disposers)];
    case 'client':
      return createClient(node, scope, disposers);
    case 'server':
      // Rendered in the browser alone, server-only content shows nothing.
      return [];
  }
}

/**
 * Browser-only content built in place. A body that throws shows the fallback
 * and reports to the nearest boundary.
 */
function createClient(
  node: ClientNode,
  scope: Scope,
  disposers: Teardown[],
): Node[] {
  const start = comment('c:client');
  const end = comment('/c:client');
  if (!node.body)
    return [start, ...createList(node.fallback, scope, disposers), end];
  const inner: Teardown[] = [];
  try {
    const nodes = createList(normaliseChildren([node.body()]), scope, inner);
    disposers.push(() => disposeAll(inner));
    return [start, ...nodes, end];
  } catch (error) {
    disposeAll(inner);
    scope.sink(error);
    return [start, ...createList(node.fallback, scope, disposers), end];
  }
}

/**
 * After a hydration: the server wrote the fallback between the markers, and
 * the body replaces it on the next microtask, once the page around it is live.
 * A body that throws leaves the fallback and reports to the nearest boundary.
 */
export function swapInClient(
  node: ClientNode,
  scope: Scope,
  disposers: Teardown[],
  end: Comment,
  fallback: Teardown[],
): void {
  const body = node.body;
  if (!body) return;
  let live = true;
  disposers.push(() => {
    live = false;
  });
  queueMicrotask(() => {
    if (!live || !end.parentNode) return;
    const inner: Teardown[] = [];
    let nodes: Node[];
    try {
      nodes = Signal.subtle.untrack(() =>
        createList(normaliseChildren([body()]), scope, inner),
      );
    } catch (error) {
      disposeAll(inner);
      scope.sink(error);
      return;
    }
    disposeAll(fallback);
    removeBetween(findStart(end, 'c:client'), end);
    insertBefore(nodes, end);
    disposers.push(() => disposeAll(inner));
  });
}

/** Renders the tree into `parent` (before `options.before` when given) and returns a disposer that removes it. */
/** The namespace content mounted into `parent` is created in: an `<svg>` or `<math>` parent's own, HTML otherwise. */
function namespaceInside(parent: ParentNode): string | undefined {
  if (!isElement(parent)) return undefined;
  const element = parent;
  if (element.namespaceURI === SVG_NS && element.localName !== 'foreignObject')
    return SVG_NS;
  if (element.namespaceURI === MATHML_NS) return MATHML_NS;
  return undefined;
}

export function mount(
  node: Instruction,
  parent: ParentNode,
  options: MountOptions = {},
): Disposer {
  const disposers: Teardown[] = [];
  const ns = namespaceInside(parent);
  const scope: Scope =
    ns === undefined
      ? { ctx: options.ctx ?? null, sink: consoleSink }
      : { ctx: options.ctx ?? null, sink: consoleSink, ns };
  const nodes = createNodes(node, scope, disposers);
  const before = options.before ?? null;
  for (const created of nodes) {
    if (before) parent.insertBefore(created, before);
    else parent.appendChild(created);
  }
  return () => {
    disposeAll(disposers);
    removeNodes(nodes);
  };
}
